import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AssistantRuntimeProvider,
  AuiConfig,
  Suggestions,
  Tools,
  useAui,
  useLocalRuntime,
  type ChatModelAdapter,
  type ChatModelRunResult,
  type ThreadAssistantMessagePart,
  type ThreadMessage,
  type ThreadMessageLike
} from '@assistant-ui/react';
import type { ReadonlyJSONObject } from 'assistant-stream/utils';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getMcpChatUrl } from '@/app/common/api/config';
import {
  getAccessToken,
  refreshAccessToken
} from '@/app/common/utils/customFetchBase';
import { toAuiSuggestions } from './config/mcpChatSuggestions';
import { useOptionalMcpChatModel } from './components/McpChatModelContext';
import { mcpChatToolkit, buildMcpChatToolkit, buildHumanToolNames, isMcpHumanToolName } from './components/mcpChatToolkit';
import type { McpChatError } from './components/McpChatErrorAlert';
import { attachPendingSuggestionMarker } from './utils/pendingSuggestionKey';
import {
  extractSuggestionRequest,
  type ChatSuggestionRequest
} from './utils/suggestionMarkers';
import { dedupePresentChartTools } from './utils/presentChartDedupe';
import useDomain from '@/hooks/useDomain';
import walletApi from '@/app/Wallet/api/walletApi';
import mcpApi, { useListMcpChatSkillsQuery } from './api/mcpApi';
import { useOptionalMcpChatOnboarding } from './hooks/McpChatOnboardingContext';
import type {
  McpActiveChatRun,
  McpChatRunSnapshot
} from './types/mcpApiTypes';
import { invalidateMcpThreadHistory } from './utils/threadHistoryEvents';

const MCP_CHAT_URL = getMcpChatUrl();

const INSUFFICIENT_WALLET_MESSAGE =
  'موجودی کیف پول AI کافی نیست. می‌توانید اعتبار AI را شارژ کنید، یا از مدل‌های رایگان Gemini با کلید API خودتان استفاده کنید.';

/** Wallet blocks that may still arrive as a plain `error` event. */
const INSUFFICIENT_WALLET_CODES = new Set([
  'INSUFFICIENT_AI_WALLET',
  'MISSING_DOMAIN_SLUG'
]);

async function invalidateWalletBalanceTags() {
  const { store } = await import('@/store/store');
  store.dispatch(walletApi.util.invalidateTags(['Wallet']));
}

async function invalidateMcpAutoBudgetTags() {
  const { store } = await import('@/store/store');
  store.dispatch(mcpApi.util.invalidateTags(['McpAutoBudget']));
}

type ChatStreamEvent =
  | { type: 'thread'; threadId: string }
  | { type: 'status'; message: string }
  | { type: 'text_delta'; delta: string }
  | { type: 'text'; text: string }
  | {
      type: 'tool_call';
      toolCallId: string;
      toolName: string;
      args: unknown;
    }
  | {
      type: 'tool_result';
      toolCallId: string;
      toolName: string;
      result: string;
      isError?: boolean;
    }
  | {
      type: 'warning';
      code: string;
      message: string;
    }
  | {
      type: 'wallet_charged';
      costUsd: number;
      costRial: number;
    }
  | {
      type: 'insufficient_wallet';
      code: string;
      message: string;
    }
  | { type: 'assistant_message'; messageId: string }
  | { type: 'error'; code?: string; message: string; reference?: string }
  | { type: 'done' }
  /** First event of every stream: the server-side run being followed. */
  | { type: 'run'; runId: string; threadId?: string | null }
  /** Last event of a run log. */
  | { type: 'run_end'; status: string };

type McpRuntimeProviderProps = {
  children: ReactNode;
  threadId?: string;
  initialMessages?: ThreadMessageLike[];
  /** Reply still generating on the server; resumed on mount. */
  activeRun?: McpActiveChatRun | null;
  /** When set on a blank chat, append+send this prompt once after mount. */
  autoSendPrompt?: string | null;
  /** Called when backend creates a thread for a brand-new chat. */
  onThreadCreated?: (threadId: string) => void;
  /** When false, help / secondary composers skip onboarding enforcement. */
  chatOnboardingGuardEnabled?: boolean;
};

function AutoSendPrompt({
  prompt,
  assertChatAllowed
}: {
  prompt: string;
  assertChatAllowed?: () => boolean;
}) {
  const aui = useAui();
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current || !prompt.trim()) return;
    if (assertChatAllowed && !assertChatAllowed()) return;
    sentRef.current = true;
    const timer = window.setTimeout(() => {
      try {
        const thread =
          typeof (aui as { thread?: unknown }).thread === 'function'
            ? (
                aui as {
                  thread: () => { append: (message: string) => void };
                }
              ).thread()
            : (
                aui as {
                  thread: { append: (message: string) => void };
                }
              ).thread;
        thread.append(prompt.trim());
      } catch (error) {
        console.error('Failed to auto-send prompt template', error);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [assertChatAllowed, aui, prompt]);

  return null;
}

function extractText(message: {
  role: string;
  content: unknown;
}): string {
  if (!Array.isArray(message.content)) {
    return typeof message.content === 'string' ? message.content : '';
  }
  const parts: string[] = [];
  for (const part of message.content) {
    if (
      part &&
      typeof part === 'object' &&
      'type' in part &&
      part.type === 'text' &&
      'text' in part
    ) {
      parts.push(String((part as { text: string }).text));
    }
  }
  return parts.join('\n').trim();
}

type ContentPart = {
  type: string;
  text?: string;
  toolCallId?: string;
  toolName?: string;
  args?: unknown;
  argsText?: string;
  result?: unknown;
};

function normalizeContent(content: unknown): ContentPart[] {
  if (!Array.isArray(content)) return [];
  return content.filter(
    (part): part is ContentPart =>
      Boolean(part && typeof part === 'object' && 'type' in part)
  );
}

/** Keep PDF bytes in the UI state, but never send them back to the model. */
function omitPdfBase64ForApi(resultText: string): string {
  if (!resultText.includes('pdfBase64')) return resultText;
  try {
    const parsed: unknown = JSON.parse(resultText);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return resultText;
    }
    if (!('pdfBase64' in parsed)) return resultText;
    const rest = { ...(parsed as Record<string, unknown>) };
    delete rest.pdfBase64;
    return JSON.stringify(rest);
  } catch {
    return resultText.replace(
      /("pdfBase64"\s*:\s*")[^"]*(")/g,
      '$1[removed]$2'
    );
  }
}

function isHitlConfigureResult(raw: string): boolean {
  try {
    const row = JSON.parse(raw) as {
      phase?: unknown;
      skillCommand?: unknown;
      promptTemplateId?: unknown;
    };
    return (
      row?.phase === 'answers' &&
      (typeof row.skillCommand === 'string' ||
        typeof row.promptTemplateId === 'string')
    );
  } catch {
    return false;
  }
}

type ApiMessage = {
  role: 'user' | 'tool';
  content: string | null;
  tool_call_id?: string;
};

/**
 * Skill form answers last sent for a user message. "Retry" drops the
 * assistant turn that carried them, so without this the skill would restart
 * from the bare user text instead of rerunning with the same inputs.
 */
type SubmittedForm = { userMessageId: string; toolMessage: ApiMessage };

function lastUserMessageId(
  messages: readonly ThreadMessage[]
): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') return messages[i]!.id;
  }
  return null;
}

function toApiMessages(messages: readonly ThreadMessage[]) {
  const out: Array<{
    role: 'user' | 'tool';
    content: string | null;
    tool_call_id?: string;
  }> = [];

  let lastUserIndex = -1;
  for (let i = 0; i < messages.length; i += 1) {
    if (messages[i]?.role === 'user') lastUserIndex = i;
  }

  if (lastUserIndex >= 0) {
    const text = attachPendingSuggestionMarker(
      extractText(messages[lastUserIndex]!)
    );
    if (text) {
      out.push({ role: 'user', content: text });
    }
  }

  // Only the HITL result on the assistant turn after this user message.
  // Older configure_* answers belong to a finished skill and must not be
  // resent with follow-ups like "خروجی PDF این گزارش رو بهم بده".
  for (let i = messages.length - 1; i > lastUserIndex; i -= 1) {
    const message = messages[i];
    if (message?.role !== 'assistant') continue;
    const toolCalls = normalizeContent(message.content).filter(
      (part) => part.type === 'tool-call'
    );
    for (let j = toolCalls.length - 1; j >= 0; j -= 1) {
      const part = toolCalls[j];
      if (part.result === undefined) continue;
      const raw =
        typeof part.result === 'string'
          ? part.result
          : JSON.stringify(part.result);
      const content = omitPdfBase64ForApi(raw);
      if (!isHitlConfigureResult(content)) continue;
      out.push({
        role: 'tool',
        tool_call_id: part.toolCallId ?? '',
        content
      });
      return out;
    }
  }

  return out;
}

type ChatRequestFields = {
  messages: ReturnType<typeof toApiMessages>;
  suggestion?: ChatSuggestionRequest;
  /** True when this turn sends a skill / prompt-template form receipt. */
  formSubmit: boolean;
};

/**
 * Request body fields for one turn. Suggestion markers leave the user text
 * and travel as the typed `suggestion` field instead.
 */
function toChatRequest(messages: readonly ThreadMessage[]): ChatRequestFields {
  const apiMessages = toApiMessages(messages);
  const formSubmit = apiMessages.some(message => message.role === 'tool');
  const userIndex = apiMessages.findIndex(message => message.role === 'user');
  const userText = apiMessages[userIndex]?.content;
  if (!userText) return { messages: apiMessages, formSubmit };

  const { content, suggestion } = extractSuggestionRequest(userText);
  const withoutMarkers = [...apiMessages];
  withoutMarkers[userIndex] = { ...apiMessages[userIndex]!, content };
  return {
    messages: withoutMarkers,
    ...(suggestion ? { suggestion } : {}),
    formSubmit
  };
}

/** Include the in-progress assistant message when continuing after a human tool. */
function messagesForApiRequest(
  messages: readonly ThreadMessage[],
  currentAssistant: ThreadMessage | undefined
): readonly ThreadMessage[] {
  if (
    !currentAssistant ||
    currentAssistant.role !== 'assistant' ||
    !Array.isArray(currentAssistant.content)
  ) {
    return messages;
  }

  const hasToolCall = currentAssistant.content.some(
    (part) => part.type === 'tool-call'
  );
  if (!hasToolCall) {
    return messages;
  }

  const alreadyIncluded = messages.some(
    (message) => message.id === currentAssistant.id
  );
  if (alreadyIncluded) {
    return messages;
  }

  return [...messages, currentAssistant];
}

async function* readSse(
  response: Response
): AsyncGenerator<ChatStreamEvent> {
  if (!response.body) {
    throw new Error('پاسخ استریم خالی است');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';

    for (const chunk of chunks) {
      const lines = chunk
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
      for (const line of lines) {
        // `id:` (resume cursor) is tracked server-side via the run snapshot;
        // `:` lines are keep-alive pings.
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload || payload === '[DONE]') continue;
        yield JSON.parse(payload) as ChatStreamEvent;
      }
    }
  }
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function serverErrorText(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as { message?: unknown; msg?: unknown };
    const message = parsed.message ?? parsed.msg;
    if (typeof message === 'string' && message.trim()) return message.trim();
  } catch {
    /* not JSON */
  }
  return raw;
}

/** Runs the request, refreshing the access token once on an expired-JWT 401. */
async function fetchChatStream(
  doFetch: () => Promise<Response>
): Promise<{ response: Response } | { errorText: string }> {
  let response = await doFetch();

  if (response.status === 401) {
    const errText = await response.text().catch(() => '');
    let code: number | undefined;
    try {
      code = (JSON.parse(errText) as { code?: number }).code;
    } catch {
      /* ignore */
    }
    const refreshed = code === 40102 ? await refreshAccessToken() : false;
    if (!refreshed) {
      return {
        errorText: `خطا در اتصال به سرور چت (401)${errText ? `: ${errText}` : ''}`
      };
    }
    response = await doFetch();
  }

  if (response.status === 409) {
    const errText = await response.text().catch(() => '');
    return {
      errorText:
        serverErrorText(errText) ||
        'پاسخ قبلی این گفتگو هنوز در حال تولید است.'
    };
  }

  if (response.status === 400) {
    // e.g. a message over the configured length cap; the server text is for users.
    const errText = await response.text().catch(() => '');
    return {
      errorText: serverErrorText(errText) || 'خطا در اتصال به سرور چت (400)'
    };
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    return {
      errorText: `خطا در اتصال به سرور چت (${response.status})${errText ? `: ${errText}` : ''}`
    };
  }

  return { response };
}

function requestRunCancel(runId: string): void {
  void fetch(`${MCP_CHAT_URL}/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    headers: authHeaders(),
    keepalive: true
  }).catch(() => {
    /* best effort */
  });
}

/**
 * The run lives on the server, so leaving the page must not stop it. Only an
 * explicit Stop (assistant-ui cancelRun → AbortError with detach=false) does.
 */
function cancelRunOnUserStop(
  abortSignal: AbortSignal,
  getRunId: () => string | null
): () => void {
  const onAbort = () => {
    const reason = abortSignal.reason as { detach?: unknown } | undefined;
    if (reason?.detach === true) return;
    const runId = getRunId();
    if (runId) requestRunCancel(runId);
  };
  abortSignal.addEventListener('abort', onAbort, { once: true });
  return () => abortSignal.removeEventListener('abort', onAbort);
}

type PendingToolCall = {
  toolCallId: string;
  toolName: string;
  args: ReadonlyJSONObject;
  argsText: string;
  result?: string;
  isError?: boolean;
};

function asArgsRecord(args: unknown): ReadonlyJSONObject {
  if (args && typeof args === 'object' && !Array.isArray(args)) {
    return args as ReadonlyJSONObject;
  }
  return {};
}

function buildAssistantContent(
  tools: PendingToolCall[],
  text: string
): ThreadAssistantMessagePart[] {
  const uniqueTools = dedupePresentChartTools(tools);
  const toolParts: ThreadAssistantMessagePart[] = uniqueTools.map(tool => ({
    type: 'tool-call' as const,
    toolCallId: tool.toolCallId,
    toolName: tool.toolName,
    args: tool.args,
    argsText: tool.argsText,
    ...(tool.result !== undefined
      ? { result: tool.result, isError: tool.isError }
      : {})
  }));
  const textParts: ThreadAssistantMessagePart[] = text
    ? [{ type: 'text' as const, text }]
    : [];

  // Skill configure: intro text above the form (backend emits text then tool_call).
  const isSkillConfigure = uniqueTools.some((tool) =>
    isMcpHumanToolName(tool.toolName)
  );
  return isSkillConfigure
    ? [...textParts, ...toolParts]
    : [...toolParts, ...textParts];
}

type FoldSeed = {
  text: string;
  tools: PendingToolCall[];
  warnings: Array<{ code: string; message: string }>;
  insufficientWallet: { code: string; message: string } | null;
  serverMessageId: string | null;
  statusText: string | null;
};

type FoldHooks = {
  onRun?: (runId: string) => void;
  onThread?: (threadId: string) => void;
};

function emptyFoldSeed(): FoldSeed {
  return {
    text: '',
    tools: [],
    warnings: [],
    insufficientWallet: null,
    serverMessageId: null,
    statusText: null
  };
}

/** Starting point for a resumed run: what the server already produced. */
function foldSeedFromSnapshot(
  snapshot: McpChatRunSnapshot | null | undefined
): FoldSeed {
  const seed = emptyFoldSeed();
  if (!snapshot) return seed;
  seed.text = snapshot.text ?? '';
  seed.tools = (snapshot.tools ?? []).map(tool => {
    const args = asArgsRecord(tool.args);
    return {
      toolCallId: tool.toolCallId,
      toolName: tool.toolName,
      args,
      argsText: JSON.stringify(args),
      ...(tool.result !== undefined
        ? { result: tool.result, isError: Boolean(tool.isError) }
        : {})
    };
  });
  seed.warnings = [...(snapshot.warnings ?? [])];
  seed.insufficientWallet = snapshot.insufficientWallet ?? null;
  seed.serverMessageId = snapshot.messageId ?? null;
  seed.statusText = snapshot.statusText ?? null;
  return seed;
}

/** Turns chat stream events into assistant-ui message updates. */
async function* foldChatEvents(
  events: AsyncIterable<ChatStreamEvent>,
  seed: FoldSeed,
  hooks: FoldHooks = {}
): AsyncGenerator<ChatModelRunResult, void> {
  let text = seed.text;
  const tools: PendingToolCall[] = seed.tools;
  const userApiKeyWarnings = seed.warnings;
  let insufficientWallet = seed.insufficientWallet;
  let chatError: McpChatError | null = null;
  let sawAssistantText = Boolean(text);
  let serverMessageId = seed.serverMessageId;
  let statusText = seed.statusText;

  // Always send `custom`: the runtime keeps the previous metadata when a
  // yield omits it, so a cleared statusText would otherwise linger.
  const yieldAssistant = (body: string) => ({
    content: buildAssistantContent(tools, body),
    metadata: {
      custom: {
        ...(serverMessageId ? { serverMessageId } : {}),
        ...(userApiKeyWarnings.length
          ? { userApiKeyWarnings: [...userApiKeyWarnings] }
          : {}),
        ...(insufficientWallet
          ? { insufficientWallet: { ...insufficientWallet } }
          : {}),
        ...(chatError ? { chatError: { ...chatError } } : {}),
        ...(statusText && !body && tools.length === 0
          ? { statusText }
          : {})
      }
    }
  });

  if (text || tools.length > 0 || statusText) {
    yield yieldAssistant(text);
  }

  for await (const event of events) {
    if (event.type === 'run') {
      hooks.onRun?.(event.runId);
      continue;
    }

    if (event.type === 'run_end') {
      continue;
    }

    if (event.type === 'thread') {
      hooks.onThread?.(event.threadId);
      continue;
    }

    if (event.type === 'assistant_message') {
      serverMessageId = event.messageId;
      if (
        sawAssistantText ||
        text ||
        tools.length > 0 ||
        userApiKeyWarnings.length > 0 ||
        insufficientWallet
      ) {
        yield yieldAssistant(text);
      }
      continue;
    }

    if (event.type === 'warning') {
      const code = typeof event.code === 'string' ? event.code.trim() : '';
      const message =
        typeof event.message === 'string' ? event.message.trim() : '';
      if (code && message && !userApiKeyWarnings.some(w => w.code === code)) {
        userApiKeyWarnings.push({ code, message });
        yield yieldAssistant(text);
      }
      continue;
    }

    if (event.type === 'insufficient_wallet') {
      const code =
        typeof event.code === 'string' && event.code.trim()
          ? event.code.trim()
          : 'INSUFFICIENT_AI_WALLET';
      const message =
        typeof event.message === 'string' && event.message.trim()
          ? event.message.trim()
          : INSUFFICIENT_WALLET_MESSAGE;
      // Shown by McpInsufficientWalletAlert, not as message text.
      insufficientWallet = { code, message };
      statusText = null;
      yield yieldAssistant(text);
      continue;
    }

    if (event.type === 'wallet_charged') {
      void invalidateWalletBalanceTags();
      continue;
    }

    if (event.type === 'status') {
      if (sawAssistantText || tools.length > 0) {
        continue;
      }
      statusText = event.message;
      yield yieldAssistant('');
      continue;
    }

    if (event.type === 'tool_call') {
      statusText = null;
      const args = asArgsRecord(event.args);
      const existing = tools.find(t => t.toolCallId === event.toolCallId);
      if (existing) {
        existing.toolName = event.toolName;
        existing.args = args;
        existing.argsText = JSON.stringify(args);
      } else {
        tools.push({
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          args,
          argsText: JSON.stringify(args)
        });
      }
      yield yieldAssistant(text);
      continue;
    }

    if (event.type === 'tool_result') {
      const existing = tools.find(t => t.toolCallId === event.toolCallId);
      if (existing) {
        existing.result = event.result;
        existing.isError = Boolean(event.isError);
      } else {
        const args = asArgsRecord({});
        tools.push({
          toolCallId: event.toolCallId,
          toolName: event.toolName,
          args,
          argsText: '{}',
          result: event.result,
          isError: Boolean(event.isError)
        });
      }
      yield yieldAssistant(text);
      continue;
    }

    if (event.type === 'text_delta') {
      sawAssistantText = true;
      statusText = null;
      text += event.delta;
      yield yieldAssistant(text);
      continue;
    }

    if (event.type === 'text') {
      sawAssistantText = true;
      statusText = null;
      text = event.text;
      yield yieldAssistant(text);
      continue;
    }

    if (event.type === 'done') {
      void invalidateMcpAutoBudgetTags();
      continue;
    }

    if (event.type === 'error') {
      if (event.code && INSUFFICIENT_WALLET_CODES.has(event.code)) {
        insufficientWallet = {
          code: event.code,
          message: event.message?.trim() || INSUFFICIENT_WALLET_MESSAGE
        };
        statusText = null;
        break;
      }
      if (event.code === 'AUTO_DAILY_BUDGET_EXHAUSTED') {
        // Rendered as a warning by McpAssistantText.
        yield {
          ...yieldAssistant(text),
          content: [
            ...buildAssistantContent(tools, text),
            { type: 'text' as const, text: event.message }
          ]
        };
        return;
      }
      // Shown by McpChatErrorAlert, not as message text.
      chatError = {
        ...(event.code ? { code: event.code } : {}),
        ...(event.reference ? { reference: event.reference } : {}),
        message: event.message?.trim() || 'پاسخی دریافت نشد.'
      };
      statusText = null;
      yield yieldAssistant(text);
      return;
    }
  }

  if (!text && tools.length === 0 && !insufficientWallet) {
    yield yieldAssistant('پاسخی دریافت نشد.');
  } else if (
    serverMessageId ||
    text ||
    tools.length > 0 ||
    insufficientWallet
  ) {
    const pendingHumanTool = tools.some(
      tool => isMcpHumanToolName(tool.toolName) && tool.result === undefined
    );
    // Keep the run paused so addResult() continues into skill API + analysis.
    yield {
      ...yieldAssistant(text),
      ...(pendingHumanTool
        ? {
            status: {
              type: 'requires-action' as const,
              reason: 'tool-calls' as const
            }
          }
        : {})
    };
  }
}

function errorResult(text: string): ChatModelRunResult {
  return { content: [{ type: 'text' as const, text }] };
}

/** Resumes `run` with the given parent: follows the server log from its cursor. */
function ResumeActiveRun({
  run,
  parentId
}: {
  run: McpActiveChatRun;
  parentId: string | null;
}) {
  const aui = useAui();
  const resumedRunIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (resumedRunIdRef.current === run.id) return;
    // Deferred so a StrictMode remount cancels the first attempt cleanly.
    const timer = window.setTimeout(() => {
      resumedRunIdRef.current = run.id;
      try {
        const thread =
          typeof (aui as { thread?: unknown }).thread === 'function'
            ? (aui as { thread: () => ResumableThread }).thread()
            : (aui as { thread: ResumableThread }).thread;
        thread.resumeRun({
          parentId,
          stream: ({ abortSignal }) => followRun(run, abortSignal)
        });
      } catch (error) {
        console.error('Failed to resume chat run', error);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [aui, run, parentId]);

  return null;
}

type ResumableThread = {
  resumeRun: (config: {
    parentId: string | null;
    stream: (options: {
      abortSignal: AbortSignal;
    }) => AsyncGenerator<ChatModelRunResult, void>;
  }) => void;
};

async function* followRun(
  run: McpActiveChatRun,
  abortSignal: AbortSignal
): AsyncGenerator<ChatModelRunResult, void> {
  const stopWatching = cancelRunOnUserStop(abortSignal, () => run.id);
  try {
    const query = run.cursor ? `?after=${encodeURIComponent(run.cursor)}` : '';
    const result = await fetchChatStream(() =>
      fetch(
        `${MCP_CHAT_URL}/runs/${encodeURIComponent(run.id)}/events${query}`,
        {
          headers: { Accept: 'text/event-stream', ...authHeaders() },
          signal: abortSignal
        }
      )
    );
    if ('errorText' in result) {
      yield errorResult(result.errorText);
      return;
    }
    yield* foldChatEvents(
      readSse(result.response),
      foldSeedFromSnapshot(run.snapshot)
    );
    invalidateMcpThreadHistory();
  } finally {
    stopWatching();
  }
}

const staticAuiConfig = AuiConfig({
  suggestions: Suggestions(toAuiSuggestions()),
  tools: Tools({ toolkit: mcpChatToolkit })
});

export function McpRuntimeProvider({
  children,
  threadId,
  initialMessages = [],
  activeRun = null,
  autoSendPrompt = null,
  onThreadCreated,
  chatOnboardingGuardEnabled = true
}: McpRuntimeProviderProps) {
  const onboarding = useOptionalMcpChatOnboarding();
  const assertChatAllowedRef = useRef<(() => boolean) | null>(null);
  assertChatAllowedRef.current =
    chatOnboardingGuardEnabled && onboarding
      ? onboarding.assertChatAllowed
      : null;
  const navigate = useNavigate();
  const { data: skillsData } = useListMcpChatSkillsQuery();
  const skillCommands = useMemo(
    () => (skillsData?.items ?? []).map((skill) => skill.command),
    [skillsData?.items]
  );
  const humanToolNames = useMemo(
    () => buildHumanToolNames(skillCommands),
    [skillCommands]
  );
  const auiConfig = useMemo(() => {
    if (skillCommands.length === 0) return staticAuiConfig;
    return AuiConfig({
      suggestions: Suggestions(toAuiSuggestions()),
      tools: Tools({ toolkit: buildMcpChatToolkit(skillCommands) })
    });
  }, [skillCommands]);
  const { domain_slug } = useDomain();
  const threadIdRef = useRef(threadId);
  const onThreadCreatedRef = useRef(onThreadCreated);
  const startedWithoutThreadRef = useRef(!threadId);
  const chatModel = useOptionalMcpChatModel();
  const modelSelectionRef = useRef(chatModel?.selection ?? null);
  const selectedModelRef = useRef(chatModel?.selectedModel ?? null);
  const domainSlugRef = useRef(domain_slug);
  const submittedFormRef = useRef<SubmittedForm | null>(null);

  threadIdRef.current = threadId;
  onThreadCreatedRef.current = onThreadCreated;
  modelSelectionRef.current = chatModel?.selection ?? null;
  selectedModelRef.current = chatModel?.selectedModel ?? null;
  domainSlugRef.current = domain_slug;

  useEffect(() => {
    threadIdRef.current = threadId;
    if (threadId) {
      startedWithoutThreadRef.current = false;
    }
  }, [threadId]);

  const adapter = useMemo<ChatModelAdapter>(
    () => ({
      async *run({ messages, abortSignal, unstable_getMessage }) {
        if (assertChatAllowedRef.current && !assertChatAllowedRef.current()) {
          return;
        }

        let runId: string | null = null;
        let createdThreadId: string | null = null;
        const stopWatching = cancelRunOnUserStop(abortSignal, () => runId);

        try {
          // Built once: it consumes the pending suggestion key, and a 401
          // retry must resend the same fields.
          const chatRequest = toChatRequest(
            messagesForApiRequest(messages, unstable_getMessage?.())
          );
          const userMessageId = lastUserMessageId(messages);
          const submittedTool = chatRequest.messages.find(
            message => message.role === 'tool'
          );
          if (userMessageId && submittedTool) {
            submittedFormRef.current = {
              userMessageId,
              toolMessage: submittedTool
            };
          } else if (
            userMessageId &&
            submittedFormRef.current?.userMessageId === userMessageId
          ) {
            // Retry of a skill turn: resend the same form answers.
            chatRequest.messages = [
              ...chatRequest.messages,
              submittedFormRef.current.toolMessage
            ];
            chatRequest.formSubmit = true;
          }
          const doFetch = () => {
            const selection = modelSelectionRef.current;
            const selectedModel = selectedModelRef.current;
            const modelOverride =
              selection?.type === 'auto'
                ? { provider: 'auto', tier: 'mid' }
                : selection?.type === 'model' && selectedModel
                  ? {
                      provider: selectedModel.provider,
                      tier: selectedModel.tier
                    }
                  : undefined;
            return fetch(MCP_CHAT_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...authHeaders()
              },
              body: JSON.stringify({
                ...chatRequest,
                threadId: threadIdRef.current,
                ...(domainSlugRef.current
                  ? { domainSlug: domainSlugRef.current }
                  : {}),
                ...(modelOverride ?? {})
              }),
              signal: abortSignal
            });
          };

          const result = await fetchChatStream(doFetch);
          if ('errorText' in result) {
            yield errorResult(result.errorText);
            return;
          }

          yield* foldChatEvents(readSse(result.response), emptyFoldSeed(), {
            onRun: id => {
              runId = id;
            },
            onThread: id => {
              threadIdRef.current = id;
              createdThreadId = id;
              onThreadCreatedRef.current?.(id);
            }
          });
        } finally {
          stopWatching();
        }

        invalidateMcpThreadHistory();
        // Update URL only after the stream finishes so React does not abort it.
        if (createdThreadId && startedWithoutThreadRef.current) {
          startedWithoutThreadRef.current = false;
          navigate(`/chat/c/${createdThreadId}`, { replace: true });
        }
      }
    }),
    [navigate]
  );

  const runtime = useLocalRuntime(adapter, {
    initialMessages,
    unstable_humanToolNames: humanToolNames,
    // configure (1) + analysis (2); leave headroom for follow-up turns
    maxSteps: 8
  });

  return (
    <TooltipProvider>
      <AssistantRuntimeProvider runtime={runtime} config={auiConfig}>
        {autoSendPrompt?.trim() ? (
          <AutoSendPrompt
            prompt={autoSendPrompt.trim()}
            assertChatAllowed={assertChatAllowedRef.current ?? undefined}
          />
        ) : null}
        {activeRun ? (
          <ResumeActiveRun
            run={activeRun}
            parentId={
              initialMessages.length > 0
                ? (initialMessages[initialMessages.length - 1]?.id ?? null)
                : null
            }
          />
        ) : null}
        {children}
      </AssistantRuntimeProvider>
    </TooltipProvider>
  );
}
